import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getCampaign, getCampaignTestCases, updateCampaignTCStatus,
  addAllTestCasesToCampaign, addTestCasesToCampaign, removeTestCaseFromCampaign,
  updateCampaign, getProject, getProjectTags,
} from '../services/api';
import { useToast } from '../App';
import Modal, { ConfirmModal } from '../components/Modal';
import {
  HiOutlinePlus, HiOutlineTrash, HiOutlinePlay, HiOutlineArrowLeft, HiOutlineTag,
} from 'react-icons/hi';
import {
  FiTarget, FiCheckCircle, FiXCircle, FiClock, FiMinusCircle, FiAlertTriangle,
  FiChevronDown, FiChevronRight,
} from 'react-icons/fi';

const STATUS_CONFIG = {
  NT: { label: 'Non Teste', icon: FiMinusCircle, color: 'var(--gray-400)', bg: 'var(--gray-100)' },
  EC: { label: 'En Cours', icon: FiClock, color: 'var(--info)', bg: 'var(--info-light)' },
  OK: { label: 'Reussi', icon: FiCheckCircle, color: 'var(--green-600)', bg: 'var(--green-100)' },
  KO: { label: 'Echoue', icon: FiXCircle, color: 'var(--danger)', bg: 'var(--danger-light)' },
  BL: { label: 'Bloque', icon: FiAlertTriangle, color: 'var(--warning)', bg: 'var(--warning-light)' },
};

const STATUT_OPTIONS = ['Planifiee', 'En cours', 'Terminee', 'Annulee'];

function StatusDropdown({ currentStatus, onChange }) {
  const [open, setOpen] = useState(false);
  const cfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.NT;
  const Icon = cfg.icon;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="btn btn-sm"
        style={{ background: cfg.bg, color: cfg.color, border: 'none', fontWeight: 500, fontSize: '0.78rem', padding: '0.25em 0.6em', borderRadius: 6 }}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      >
        <Icon style={{ marginRight: 3 }} />{cfg.label}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 50,
          background: 'var(--card-bg, #fff)', borderRadius: 8, boxShadow: 'var(--shadow-lg)',
          minWidth: 140, padding: 4, marginTop: 4,
        }}>
          {Object.entries(STATUS_CONFIG).map(([key, val]) => {
            const I = val.icon;
            return (
              <button key={key} className="dropdown-item d-flex align-items-center gap-2" style={{ fontSize: '0.8rem', padding: '0.4em 0.8em', borderRadius: 4 }}
                onClick={(e) => { e.stopPropagation(); onChange(key); setOpen(false); }}>
                <I style={{ color: val.color }} /> {val.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CampaignDetail() {
  const { id: projectId, campaignId } = useParams();
  const addToast = useToast();
  const [campaign, setCampaign] = useState(null);
  const [items, setItems] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [expandedReqs, setExpandedReqs] = useState({});
  const [commentModal, setCommentModal] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [addingAll, setAddingAll] = useState(false);
  const [showAddSelect, setShowAddSelect] = useState(false);
  const [selectableTC, setSelectableTC] = useState([]);
  const [selectedTCIds, setSelectedTCIds] = useState([]);
  const [projectTags, setProjectTags] = useState([]);
  const [addFilterTag, setAddFilterTag] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [campRes, itemsRes, projRes] = await Promise.all([
        getCampaign(projectId, campaignId),
        getCampaignTestCases(projectId, campaignId),
        getProject(projectId),
      ]);
      setCampaign(campRes.data);
      setItems(itemsRes.data);
      setProject(projRes.data);
    } catch { /* interceptor */ }
    setLoading(false);
  }, [projectId, campaignId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getProjectTags(projectId).then(({ data }) => setProjectTags(data)).catch(() => {});
  }, [projectId]);

  // Regrouper les items par exigence
  const groupedByReq = {};
  items.forEach(item => {
    const key = item.requirement_exg_id || 'Sans exigence';
    if (!groupedByReq[key]) {
      groupedByReq[key] = { exg_id: item.requirement_exg_id, exigence: item.requirement_exigence, items: [] };
    }
    groupedByReq[key].items.push(item);
  });

  const filteredGroups = {};
  Object.entries(groupedByReq).forEach(([key, group]) => {
    const filteredItems = filter === 'ALL' ? group.items : group.items.filter(i => i.statut === filter);
    if (filteredItems.length > 0) {
      filteredGroups[key] = { ...group, items: filteredItems };
    }
  });

  const handleStatusChange = async (item, newStatus) => {
    if (newStatus === 'KO' || newStatus === 'BL') {
      setCommentModal({ item, statut: newStatus });
      setCommentText('');
      return;
    }
    try {
      await updateCampaignTCStatus(projectId, campaignId, item.id, { statut: newStatus });
      load();
    } catch { addToast('error', 'Erreur lors du changement de statut.'); }
  };

  const handleCommentSubmit = async () => {
    if (!commentModal) return;
    try {
      await updateCampaignTCStatus(projectId, campaignId, commentModal.item.id, {
        statut: commentModal.statut, commentaire: commentText,
      });
      setCommentModal(null);
      load();
    } catch { addToast('error', 'Erreur lors du changement de statut.'); }
  };

  const handleAddAll = async () => {
    setAddingAll(true);
    try {
      const res = await addAllTestCasesToCampaign(projectId, campaignId);
      addToast('success', `${res.data.added} cas de test ajoutes.`, 'Ajoutes');
      load();
    } catch { addToast('error', 'Erreur lors de l\'ajout.'); }
    setAddingAll(false);
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    try {
      await removeTestCaseFromCampaign(projectId, campaignId, confirmRemove);
      setConfirmRemove(null);
      load();
    } catch { addToast('error', 'Erreur lors de la suppression.'); }
  };

  const handleStartCampaign = async () => {
    try {
      await updateCampaign(projectId, campaignId, { statut: 'En cours' });
      load();
      addToast('success', 'Campagne demarree.', 'Demarree');
    } catch { addToast('error', 'Erreur.'); }
  };

  const handleFinishCampaign = async () => {
    try {
      await updateCampaign(projectId, campaignId, { statut: 'Terminee' });
      load();
      addToast('success', 'Campagne terminee.', 'Terminee');
    } catch { addToast('error', 'Erreur.'); }
  };

  // Ajout selectif
  const openAddSelect = () => {
    if (!project) return;
    const existingTcIds = new Set(items.map(i => i.test_case_id));
    const available = [];
    (project.requirements || []).forEach(r => {
      (r.test_cases || []).forEach(tc => {
        if (!existingTcIds.has(tc.id)) {
          available.push({ ...tc, req_exg_id: r.exg_id, req_exigence: r.exigence });
        }
      });
    });
    setSelectableTC(available);
    setSelectedTCIds([]);
    setShowAddSelect(true);
  };

  const handleAddSelected = async () => {
    if (selectedTCIds.length === 0) return;
    try {
      const res = await addTestCasesToCampaign(projectId, campaignId, selectedTCIds);
      addToast('success', `${res.data.added} cas de test ajoutes.`, 'Ajoutes');
      setShowAddSelect(false);
      load();
    } catch { addToast('error', 'Erreur.'); }
  };

  const toggleSelectTC = (tcId) => {
    setSelectedTCIds(prev => prev.includes(tcId) ? prev.filter(id => id !== tcId) : [...prev, tcId]);
  };

  if (loading) return (
    <div className="loading-spinner"><output className="spinner-border"></output><span>Chargement...</span></div>
  );

  if (!campaign) return <div className="text-muted p-4">Campagne introuvable.</div>;

  const total = campaign.total || 0;
  const executed = campaign.ok + campaign.ko;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <Link to={`/projects/${projectId}/campaigns`} className="text-muted text-decoration-none d-flex align-items-center gap-1 mb-1" style={{ fontSize: '0.82rem' }}>
            <HiOutlineArrowLeft /> Retour aux campagnes
          </Link>
          <h2 className="d-flex align-items-center gap-2">
            <FiTarget style={{ color: 'var(--primary)' }} />
            {campaign.name}
          </h2>
          <div className="d-flex align-items-center gap-2 mt-1">
            {campaign.version && <span className="badge" style={{ background: 'var(--navy-600)', color: '#fff', fontSize: '0.75rem' }}>{campaign.version}</span>}
            <span className="badge" style={{ background: 'var(--gray-100)', color: 'var(--gray-600)', fontSize: '0.75rem' }}>{campaign.environment}</span>
            <span className="text-muted" style={{ fontSize: '0.8rem' }}>par {campaign.created_by_username}</span>
          </div>
        </div>
        <div className="d-flex gap-2">
          {campaign.statut === 'Planifiee' && (
            <button className="btn btn-primary btn-sm" onClick={handleStartCampaign} disabled={total === 0}>
              <HiOutlinePlay /> Demarrer
            </button>
          )}
          {campaign.statut === 'En cours' && (
            <button className="btn btn-success btn-sm" onClick={handleFinishCampaign}>
              <FiCheckCircle /> Terminer
            </button>
          )}
        </div>
      </div>
      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-2">
          <div className="stat-card">
            <div className="stat-content" style={{ textAlign: 'center' }}>
              <div className="stat-number">{total}</div>
              <div className="stat-label">Total</div>
            </div>
          </div>
        </div>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          const count = campaign[key.toLowerCase()] || 0;
          return (
            <div key={key} className="col-md-2">
              <button type="button" className="stat-card"
                style={{ cursor: 'pointer', border: filter === key ? `2px solid ${cfg.color}` : undefined, background: 'none', font: 'inherit', color: 'inherit', textAlign: 'left', width: '100%', padding: 0 }}
                onClick={() => setFilter(filter === key ? 'ALL' : key)}>
                <div className="stat-icon" style={{ background: cfg.bg, color: cfg.color }}><Icon /></div>
                <div className="stat-content">
                  <div className="stat-number">{count}</div>
                  <div className="stat-label">{cfg.label}</div>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* Barre de progression globale */}
      <div className="card mb-4" style={{ padding: '1rem 1.2rem' }}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Progression</span>
          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--navy-500)' }}>
            {total > 0 ? ((executed / total) * 100).toFixed(0) : 0}%
          </span>
        </div>
        <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: 'var(--gray-200)' }}>
          {campaign.ok > 0 && <div style={{ width: `${campaign.ok / total * 100}%`, background: 'var(--green-500)', transition: 'width 0.3s' }} />}
          {campaign.ko > 0 && <div style={{ width: `${campaign.ko / total * 100}%`, background: 'var(--danger)', transition: 'width 0.3s' }} />}
          {campaign.ec > 0 && <div style={{ width: `${campaign.ec / total * 100}%`, background: 'var(--info)', transition: 'width 0.3s' }} />}
          {campaign.bl > 0 && <div style={{ width: `${campaign.bl / total * 100}%`, background: 'var(--warning)', transition: 'width 0.3s' }} />}
        </div>
      </div>

      {/* Actions ajout */}
      <div className="d-flex gap-2 mb-3">
        <button className="btn btn-outline-primary btn-sm" onClick={openAddSelect}>
          <HiOutlinePlus /> Ajouter des cas de test
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={handleAddAll} disabled={addingAll}>
          {addingAll ? 'Ajout...' : 'Ajouter tous les CT du projet'}
        </button>
        {filter !== 'ALL' && (
          <button className="btn btn-outline-secondary btn-sm ms-auto" onClick={() => setFilter('ALL')}>
            Afficher tout ({items.length})
          </button>
        )}
      </div>

      {/* Liste des CT groupes par exigence */}
      {Object.keys(filteredGroups).length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FiTarget /></div>
          <p>{items.length === 0 ? 'Aucun cas de test dans cette campagne. Ajoutez-en pour commencer.' : 'Aucun resultat pour ce filtre.'}</p>
        </div>
      ) : (
        Object.entries(filteredGroups).map(([key, group]) => {
          const isExpanded = expandedReqs[key] !== false;
          return (
            <div key={key} className="card mb-2">
              <button type="button"
                className="card-header d-flex align-items-center gap-2"
                style={{ cursor: 'pointer', padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 600, background: 'none', border: 'none', font: 'inherit', color: 'inherit', textAlign: 'left', width: '100%' }}
                onClick={() => setExpandedReqs(prev => ({ ...prev, [key]: !isExpanded }))}
              >
                {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                <span style={{ color: 'var(--navy-500)' }}>{group.exg_id}</span>
                <span className="text-muted" style={{ fontWeight: 400 }}>— {group.exigence}</span>
                <span className="ms-auto badge" style={{ background: 'var(--gray-100)', color: 'var(--gray-600)', fontSize: '0.72rem' }}>
                  {group.items.length} CT
                </span>
              </button>
              {isExpanded && (
                <div className="table-responsive">
                  <table className="table table-sm mb-0" style={{ fontSize: '0.82rem' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>ID</th>
                        <th>Cas de test</th>
                        <th style={{ width: 120 }}>Statut</th>
                        <th style={{ width: 140 }}>Execute par</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map(item => (
                        <tr key={item.id}>
                          <td><code style={{ fontSize: '0.78rem' }}>{item.ct_id}</code></td>
                          <td>
                            <div>{item.cas_test}</div>
                            {item.commentaire && (
                              <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>
                                {item.commentaire}
                              </div>
                            )}
                          </td>
                          <td>
                            <StatusDropdown
                              currentStatus={item.statut}
                              onChange={(newStatus) => handleStatusChange(item, newStatus)}
                            />
                          </td>
                          <td style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                            {item.executed_by_username || '—'}
                          </td>
                          <td>
                            <button className="btn btn-sm" style={{ color: 'var(--danger)', padding: '0.15em 0.3em' }}
                              onClick={() => setConfirmRemove(item.id)} title="Retirer de la campagne">
                              <HiOutlineTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })
      )}
      {/* Modal commentaire KO/BL */}
      <Modal show={!!commentModal} onClose={() => setCommentModal(null)} title="Commentaire requis">
        <p style={{ fontSize: '0.85rem' }}>
          Veuillez ajouter un commentaire pour le statut <strong>{commentModal?.statut}</strong> du CT <strong>{commentModal?.item?.ct_id}</strong>.
        </p>
        <textarea className="form-control mb-3" rows="3" value={commentText} onChange={e => setCommentText(e.target.value)}
          placeholder="Decrivez le probleme rencontre..." autoFocus />
        <div className="d-flex gap-2 justify-content-end">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setCommentModal(null)}>Annuler</button>
          <button className="btn btn-primary btn-sm" onClick={handleCommentSubmit} disabled={!commentText.trim()}>Confirmer</button>
        </div>
      </Modal>

      {/* Modal ajout selectif */}
      <Modal show={showAddSelect} onClose={() => { setShowAddSelect(false); setAddFilterTag(null); }} title="Ajouter des cas de test" icon={<HiOutlinePlus />} size="lg">
        {selectableTC.length === 0 ? (
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>Tous les cas de test du projet sont deja dans cette campagne.</p>
        ) : (
          <>
            {/* Tag filter bar */}
            {projectTags.length > 0 && (
              <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <HiOutlineTag style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }} />
                <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>Filtrer par tag :</span>
                {projectTags.map(t => (
                  <button
                    key={t.id}
                    className={`tag-chip-btn ${addFilterTag === t.id ? 'tag-chip-selected' : ''}`}
                    style={{ '--tag-color': t.color }}
                    onClick={() => setAddFilterTag(addFilterTag === t.id ? null : t.id)}
                  >
                    {t.name}
                  </button>
                ))}
                {addFilterTag && (
                  <button className="btn btn-outline-primary btn-sm ms-2" style={{ fontSize: '0.72rem' }} onClick={() => {
                    const taggedIds = selectableTC.filter(tc => (tc.tags || []).some(t => t.id === addFilterTag)).map(tc => tc.id);
                    setSelectedTCIds(prev => {
                      const s = new Set(prev);
                      taggedIds.forEach(id => s.add(id));
                      return [...s];
                    });
                  }}>
                    Selectionner ce tag ({selectableTC.filter(tc => (tc.tags || []).some(t => t.id === addFilterTag)).length})
                  </button>
                )}
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ fontSize: '0.82rem' }}>
                {addFilterTag
                  ? `${selectableTC.filter(tc => (tc.tags || []).some(t => t.id === addFilterTag)).length} / ${selectableTC.length} affiche(s)`
                  : `${selectableTC.length} disponible(s)`
                }
              </span>
              <button className="btn btn-outline-primary btn-sm" onClick={() => {
                const visible = addFilterTag ? selectableTC.filter(tc => (tc.tags || []).some(t => t.id === addFilterTag)) : selectableTC;
                const visibleIds = visible.map(tc => tc.id);
                const allSelected = visibleIds.every(id => selectedTCIds.includes(id));
                if (allSelected) setSelectedTCIds(prev => prev.filter(id => !visibleIds.includes(id)));
                else setSelectedTCIds(prev => { const s = new Set(prev); visibleIds.forEach(id => s.add(id)); return [...s]; });
              }}>
                Tout selectionner / deselectionner
              </button>
            </div>
            <div style={{ maxHeight: 350, overflow: 'auto' }}>
              {selectableTC
                .filter(tc => !addFilterTag || (tc.tags || []).some(t => t.id === addFilterTag))
                .map(tc => (
                <label key={tc.id} className="d-flex align-items-center gap-2 py-1 px-2" style={{ cursor: 'pointer', fontSize: '0.82rem', borderRadius: 4 }}>
                  <input type="checkbox" checked={selectedTCIds.includes(tc.id)} onChange={() => toggleSelectTC(tc.id)} />
                  <code style={{ fontSize: '0.75rem', color: 'var(--navy-500)' }}>{tc.ct_id}</code>
                  <span className="text-truncate">{tc.cas_test}</span>
                  <span className="d-flex gap-1 ms-auto flex-shrink-0">
                    {(tc.tags || []).map(t => (
                      <span key={t.id} className="tag-chip" style={{ '--tag-color': t.color }}>{t.name}</span>
                    ))}
                  </span>
                  <span className="text-muted flex-shrink-0" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{tc.req_exg_id}</span>
                </label>
              ))}
            </div>
            <div className="d-flex gap-2 justify-content-end mt-3">
              <button className="btn btn-outline-secondary btn-sm" onClick={() => { setShowAddSelect(false); setAddFilterTag(null); }}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={handleAddSelected} disabled={selectedTCIds.length === 0}>
                Ajouter {selectedTCIds.length > 0 ? `(${selectedTCIds.length})` : ''}
              </button>
            </div>
          </>
        )}
      </Modal>

      <ConfirmModal
        show={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={handleRemove}
        title="Retirer le cas de test"
        message="Retirer ce cas de test de la campagne ? Le resultat d'execution sera perdu."
        confirmText="Retirer"
      />
    </div>
  );
}
